import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPractitionerDashboard } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { DoorOpen, CreditCard, FileText, Key, MessageSquare, ArrowRight, Loader2, BookOpen, Info } from 'lucide-react';

function ActionCard({ icon: Icon, title, subtitle, badge, badgeColor, onClick, testId }) {
  return (
    <button onClick={onClick}
      className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-5 text-left hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-300 w-full"
      data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F2F0EB] flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#5C5C55]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">{title}</p>
            <p className="text-xs text-[#8A8B85]">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge && <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${badgeColor}`}>{badge}</span>}
          <ArrowRight className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
        </div>
      </div>
    </button>
  );
}

export default function PractitionerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getPractitionerDashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  const studio = data?.studio;
  const contract = data?.contract;
  const nextPayment = data?.next_payment;
  const access = data?.access;

  return (
    <div className="animate-fade-in" data-testid="practitioner-dashboard">
      <div className="mb-8">
        <p className="text-sm text-[#5C5C55]">Welcome back</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mt-1">{user?.name}</h1>
      </div>

      {/* Studio info */}
      {studio && (
        <div className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6 mb-6" data-testid="my-studio-info">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E8EDE5] flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium text-[#1A1A1A]">{studio.name}</h2>
              <p className="text-xs text-[#8A8B85]">{studio.description}</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-[#F2F0EB]">
            <span className="text-lg font-heading font-medium text-[#1A1A1A]">£{studio.monthly_rent?.toFixed(0)}<span className="text-xs text-[#8A8B85] font-body">/month</span></span>
            <span className="text-xs px-3 py-1 rounded-full bg-[#E8EDE5] text-[#3A4035] font-semibold capitalize">{studio.status}</span>
          </div>
        </div>
      )}

      {/* Next payment */}
      {nextPayment && (
        <div className={`rounded-2xl p-5 mb-6 ${nextPayment.status === 'overdue' ? 'bg-[#F5E6E6]' : 'bg-[#F4EFE6]'}`} data-testid="next-payment-alert">
          <div className="flex items-center gap-3">
            <CreditCard className={`w-5 h-5 ${nextPayment.status === 'overdue' ? 'text-[#8A3D3D]' : 'text-[#7A6849]'}`} strokeWidth={1.5} />
            <div>
              <p className={`text-sm font-medium ${nextPayment.status === 'overdue' ? 'text-[#8A3D3D]' : 'text-[#7A6849]'}`}>
                {nextPayment.status === 'overdue' ? 'Payment Overdue' : 'Next Payment Due'}
              </p>
              <p className={`text-xs ${nextPayment.status === 'overdue' ? 'text-[#8A3D3D]/70' : 'text-[#7A6849]/70'}`}>
                £{nextPayment.amount?.toFixed(0)} — Due {nextPayment.due_date}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="space-y-3">
        <ActionCard icon={CreditCard} title="Payments" subtitle="View payment history" onClick={() => navigate('/my-payments')}
          badge={nextPayment ? `£${nextPayment.amount?.toFixed(0)} due` : undefined}
          badgeColor={nextPayment?.status === 'overdue' ? 'bg-[#F5E6E6] text-[#8A3D3D]' : 'bg-[#F4EFE6] text-[#7A6849]'}
          testId="action-payments" />

        <ActionCard icon={FileText} title="Contract" subtitle="Licence agreement" onClick={() => navigate('/my-contract')}
          badge={contract?.status} badgeColor={contract?.status === 'active' ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#F4EFE6] text-[#7A6849]'}
          testId="action-contract" />

        <ActionCard icon={Key} title="Access" subtitle="Digital key status" onClick={() => navigate('/my-access')}
          badge={access?.status} badgeColor={access?.status === 'active' ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#F4EFE6] text-[#7A6849]'}
          testId="action-access" />

        <ActionCard icon={MessageSquare} title="Support" subtitle={`${data?.open_tickets || 0} open ticket${data?.open_tickets !== 1 ? 's' : ''}`}
          onClick={() => navigate('/report-issue')}
          badge={data?.open_tickets > 0 ? `${data.open_tickets} open` : undefined}
          badgeColor="bg-[#F5E6E6] text-[#8A3D3D]"
          testId="action-support" />

        <ActionCard icon={BookOpen} title="House Rules" subtitle="Studio guidelines" onClick={() => navigate('/house-rules')} testId="action-house-rules" />

        <ActionCard icon={Info} title="Welcome Guide" subtitle="Getting started info" onClick={() => navigate('/welcome')} testId="action-welcome" />
      </div>
    </div>
  );
}
